/* Expiry and reminders on the contract page (pages/legal/LegalContractDetail.js).
 * The same hook as HMS's, so the two systems behave alike.
 *
 * Every call answers with the full contract detail payload, which the page
 * swaps in with ``onUpdated``; ``onMessage({ kind, message })`` is the page's
 * flash. Who may change the expiry comes from the server
 * (``can_change_expiry``) and is re-checked there; anyone who opens the
 * contract may set their own reminder.
 */
import { useEffect, useState } from 'react';

import legalContractService, { legalRefusal } from '../../services/legalContractService';
import { formatDay } from './contractExpiry';

export default function useContractExpiry({ contract, onUpdated, onMessage }) {
  // 'expiry' | 'reminder' | reminder id (removing) while a request runs.
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [noExpiry, setNoExpiry] = useState(false);
  const [remindOn, setRemindOn] = useState('');
  const [note, setNote] = useState('');

  // The page stays mounted when it moves to another contract (the paired
  // banner's View Contract, Back/Forward), so nothing typed for one contract
  // may carry over onto the next.
  const contractId = contract?.id;
  useEffect(() => {
    setEditing(false);
    setExpiryDate('');
    setNoExpiry(false);
    setRemindOn('');
    setNote('');
  }, [contractId]);

  const act = async (key, call, success, fallback) => {
    setBusy(key);
    try {
      const data = await call();
      onUpdated(data);
      onMessage({ kind: 'success', message: success(data) });
      return true;
    } catch (err) {
      onMessage({ kind: 'error', message: legalRefusal(err, fallback) });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const startEdit = () => {
    setExpiryDate(contract.expiry_date || '');
    setNoExpiry(Boolean(contract.no_expiry));
    setEditing(true);
  };

  const saveExpiry = async () => {
    const done = await act(
      'expiry',
      () => legalContractService.setExpiry(contract.id, {
        expiry_date: noExpiry ? null : expiryDate || null,
        no_expiry: noExpiry,
      }),
      (data) => (data.no_expiry
        ? 'Saved: no expiry / rolls over.'
        : data.expiry_date
          ? `Saved: expires ${formatDay(data.expiry_date)}.`
          : 'Expiry date cleared.'),
      'Could not save the expiry date. Please try again.',
    );
    if (done) setEditing(false);
  };

  const addReminder = async () => {
    const done = await act(
      'reminder',
      () => legalContractService.addReminder(contract.id, { remind_on: remindOn, note }),
      () => `Reminder set for ${formatDay(remindOn)}. You will get a bell that day.`,
      'Could not set the reminder. Please try again.',
    );
    if (done) { setRemindOn(''); setNote(''); }
  };

  const removeReminder = (reminder) => act(
    reminder.id,
    () => legalContractService.removeReminder(contract.id, reminder.id),
    () => 'Reminder removed.',
    'Could not remove the reminder. Please try again.',
  );

  return {
    busy, editing, setEditing, startEdit,
    expiryDate, setExpiryDate: (v) => { setExpiryDate(v || ''); if (v) setNoExpiry(false); },
    noExpiry, setNoExpiry: (v) => { setNoExpiry(v); if (v) setExpiryDate(''); },
    saveExpiry, remindOn, setRemindOn, note, setNote, addReminder, removeReminder,
  };
}
